import { countOccupants } from 'lib/offer/occupancyUtils'
import { nonNullable } from 'lib/array/arrayUtils'
import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'

interface Props {
  /**
   * @default adult
   */
  adultLabel?: 'adult' | 'traveller'
  occupancies: Array<App.Occupants | App.RoomOccupants>
  /**
   * @default room
   */
  saleUnit?: string
}

function FormattedOccupancy(props: Props) {
  const { adultLabel = 'adult', occupancies, saleUnit = 'room' } = props
  const intl = useIntl()

  const { adults, children, infants, teenagers, roomCount } = useMemo(() => {
    const filteredOccupants = nonNullable(Array.isArray(occupancies) ? occupancies : [occupancies])
    const counts = countOccupants(filteredOccupants)
    return {
      ...counts,
      roomCount: filteredOccupants.length,
    }
  }, [occupancies])

  const formattedMessage = useMemo(() => {
    if (adults === 0 && children === 0 && infants === 0 && teenagers === 0) {
      return ''
    }

    // Format each occupant type separately using ICU
    const adultText = adults > 0
      ? (adultLabel === 'adult'
          ? intl.formatMessage(
              {
                id: '-p9rRkJ8wjooryQpSduU',
                defaultMessage: '{count, plural, one {# adult} other {# adults}}',
              },
              { count: adults },
            )
          : intl.formatMessage(
              {
                id: '9B_yJoiuIJLihXkuDH5n',
                defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
              },
              { count: adults },
            ))
      : null

    const teenagerText = teenagers > 0
      ? intl.formatMessage(
          {
            id: 'kpIl-mGXr5b1GBaWHBE3',
            defaultMessage: '{count, plural, one {# teenager} other {# teenagers}}',
          },
          { count: teenagers },
        )
      : null

    const childText = children > 0
      ? intl.formatMessage(
          {
            id: 'Rp3fR_M0wQDlJTCpAs3x',
            defaultMessage: '{count, plural, one {# child} other {# children}}',
          },
          { count: children },
        )
      : null

    const infantText = infants > 0
      ? intl.formatMessage(
          {
            id: '1fklQTOtKX01uIhEmJzy',
            defaultMessage: '{count, plural, one {# infant} other {# infants}}',
          },
          { count: infants },
        )
      : null

    // Combine parts - filter out nulls and join with commas
    // This is acceptable as it's formatting logic, not translation logic
    const parts = [adultText, teenagerText, childText, infantText].filter((part): part is string => part !== null)
    const occupantsText = parts.join(', ')

    // Format rooms if saleUnit is provided
    if (!saleUnit) {
      return intl.formatMessage(
        {
          id: 'u7ml7GrflwZ4zVnmUMdj',
          defaultMessage: '<strong>{occupants}</strong>',
        },
        {
          occupants: occupantsText,
          strong: (chunks: React.ReactNode) => <strong key="occupants">{chunks}</strong>,
        },
      )
    }

    const roomsText = intl.formatMessage(
      {
        id: 'e8dxqwJpS1y7Cc36gv08',
        defaultMessage: '({saleUnitCount})',
      },
      { saleUnitCount: formatSaleUnitCount(intl, saleUnit, roomCount) },
    )

    return intl.formatMessage(
      {
        id: 'S9q_ksPi92QCTAfKkxTy',
        defaultMessage: '<strong>{occupants}</strong> {rooms}',
      },
      {
        occupants: occupantsText,
        rooms: roomsText,
        strong: (chunks: React.ReactNode) => <strong key="occupants">{chunks}</strong>,
      },
    )
  }, [intl, adults, children, infants, teenagers, adultLabel, saleUnit, roomCount])

  if (!formattedMessage) {
    return null
  }

  return <>{formattedMessage}</>
}

export default FormattedOccupancy
